import type { FC, PropsWithChildren } from 'react';

import { convertToLocalTime } from '@shared/utils/date.utils';
import { getFormattedDateParts } from '@shared/utils/formatters.utils';

export const MultiLineEllipsisCellRenderer: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex items-center h-full" data-testid="multi-line-ellipsis-cell-renderer-root">
      <div
        className="line-clamp-2 leading-4 whitespace-normal wrap-break-word"
        data-testid="multi-line-ellipsis-cell-renderer-content"
      >
        {children}
      </div>
    </div>
  );
};

export const EmailCellRenderer = ({ email = '' }: { email: string }) => {
  if (!email) return null;

  const EMAIL_REGEX = /([.@-_])/g;

  const visualEmail = email.replaceAll(EMAIL_REGEX, '\u200B$1');

  return (
    <MultiLineEllipsisCellRenderer>
      <>
        <span aria-hidden="true" data-testid="email-cell-renderer-visual">
          {visualEmail}
        </span>
        <span className="sr-only" data-testid="email-cell-renderer-raw">
          {email}
        </span>
      </>
    </MultiLineEllipsisCellRenderer>
  );
};

export const DateRange = ({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) => {
  const dateFromFormatted = convertToLocalTime(dateFrom);
  const dateToFormatted = convertToLocalTime(dateTo);
  return (
    <MultiLineEllipsisCellRenderer>
      <>
        <span className="whitespace-nowrap" data-testid="date-range-from">
          {dateFromFormatted} -{' '}
        </span>{' '}
        <span className="whitespace-nowrap" data-testid="date-range-to">
          {dateToFormatted}
        </span>
      </>
    </MultiLineEllipsisCellRenderer>
  );
};

export const DateColumnOnTwoLines = ({ dateString, hour12 = false }: { dateString: string; hour12?: boolean }) => {
  const dateParts = getFormattedDateParts(dateString, hour12);
  if (!dateParts) return '--';
  const { date, time } = dateParts;
  return (
    <MultiLineEllipsisCellRenderer>
      <>
        <span className="whitespace-nowrap" data-testid="date-column-on-two-lines-date">
          {date},
        </span>{' '}
        <span className="whitespace-nowrap" data-testid="date-column-on-two-lines-time">
          {time}
        </span>
      </>
    </MultiLineEllipsisCellRenderer>
  );
};

export const DateColumn = ({ dateString, hour12 = true }: { dateString: string; hour12?: boolean }) => {
  const dateParts = getFormattedDateParts(dateString, hour12);
  if (!dateParts) return '--';
  const { date, time } = dateParts;
  return (
    <span data-testid="date-column-value">
      {date}, {time}
    </span>
  );
};
